import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult, PaginatedResponse } from '@synergy/api';
import { Dispatch } from 'redux';
import { DocumentType, ReceiptAlert } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { downloadFile } from '@synergy/utils';
import { paloIsRefused } from '@synergy/components-palo';

import { Api } from '../../api';
import { AppState } from '../../store/models/app-state';
import { Demand } from './models/demand';
import { resourceMissing } from '../../common/reducers/resource-missing-reducer';

export const CREATE_DEMAND = 'CREATE_DEMAND';
export const DELETE_DEMAND = 'DELETE_DEMAND';
export const DUPLICATE_DEMAND = 'DUPLICATE_DEMAND';
export const FINISH_DEMAND_SUCCESS = 'FINISH_DEMAND_SUCCESS';
export const RECEIVE_DEMAND = 'RECEIVE_DEMAND';
export const RECEIVE_DEMANDS = 'RECEIVE_DEMANDS';
export const REQUEST_DEMAND = 'REQUEST_DEMAND';
export const REQUEST_DEMAND_END = 'REQUEST_DEMAND_END';
export const REQUEST_DEMAND_START = 'REQUEST_DEMAND_START';
export const REQUEST_DEMANDS = 'REQUEST_DEMANDS';
export const RESET_DEMAND = 'RESET_DEMAND';
export const RESET_DEMANDS = 'RESET_DEMANDS';
export const START_DEMAND_SUCCESS = 'START_DEMAND_SUCCESS';
export const UPDATE_DEMAND = 'UPDATE_DEMAND';
export const UPDATE_DEMAND_ALERT = 'UPDATE_DEMAND_ALERT';
export const ABORT_DEMAND_SUCCESS = 'ABORT_DEMAND_SUCCESS';

const requestStart = () => {
    return {
        type: REQUEST_DEMAND_START,
    };
};

export const requestEnd = () => {
    return {
        type: REQUEST_DEMAND_END,
    };
};

const requestDemands = () => {
    return {
        type: REQUEST_DEMANDS,
    };
};

const requestDemand = () => {
    return {
        type: REQUEST_DEMAND,
    };
};

const createDemand = () => {
    return {
        type: CREATE_DEMAND,
    };
};

const updateDemand = () => {
    return {
        type: UPDATE_DEMAND,
    };
};

const deleteDemandAction = (demandId: number) => {
    return {
        type: DELETE_DEMAND,
        demandId,
    };
};

const finishDemandSuccess = () => {
    return {
        type: FINISH_DEMAND_SUCCESS,
    };
};

const startDemandSuccess = () => {
    return {
        type: START_DEMAND_SUCCESS,
    };
};

export const resetDemands = () => {
    return {
        type: RESET_DEMANDS,
    };
};

export const receiveDemands = (demands: PaginatedResponse<Demand>) => {
    return {
        type: RECEIVE_DEMANDS,
        demands,
    };
};

export const resetDemand = () => {
    return {
        type: RESET_DEMAND,
    };
};

export const receiveDemand = (entity: Demand) => {
    return {
        type: RECEIVE_DEMAND,
        entity,
    };
};

const demandAlertUpdated = (alert?: ReceiptAlert) => {
    return {
        type: UPDATE_DEMAND_ALERT,
        alert,
    };
};

const scheduledDemandAborted = (demandId: number) => {
    return {
        type: ABORT_DEMAND_SUCCESS,
        demandId,
    };
};

export const fetchDemands = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestStart());
        dispatch(requestDemands());

        const response = await api.demands.cget(searchParams);
        if (response.success) {
            dispatch(receiveDemands(response.result));
            dispatch(requestEnd());
        } else {
            dispatch(requestEnd());
            // The list says a refused filter in its own body, with the cause
            // and a way out; the toast would say it again without either.
            if (!paloIsRefused(searchParams.toString())) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            }
        }
    };
};

export const startDemand = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.demands.start(id);

        if (response.success) {
            dispatch(startDemandSuccess());
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const finishDemand = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.demands.complete(id);

        if (response.success) {
            dispatch(finishDemandSuccess());
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const fetchDemand = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestDemand());

        const response = await api.demands.get(id);
        if (response.success) {
            dispatch(receiveDemand(response.result));
        } else if (404 === response.errorCode) {
            // Not a fault to report in passing: the address names a record
            // that does not exist, and the mask says so instead of standing empty.
            dispatch(requestEnd());
            dispatch(resourceMissing());
        } else {
            dispatch(requestEnd());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const duplicateDemand = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: DUPLICATE_DEMAND });

        const response = await api.demands.copyDemand(id);
        dispatch(requestEnd());

        if (response.success) {
            dispatch(receiveDemand(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const createOrUpdateDemand = (demand: Partial<Demand>, demandId?: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        let response: ApiResult<Demand>;

        if (demandId) {
            dispatch(updateDemand());
            response = await api.demands.patch(demandId, demand);
        } else {
            dispatch(createDemand());
            response = await api.demands.post(demand as Demand);
        }

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.demandSavedSuccess')));
            dispatch(receiveDemand(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.demandSavedError')));
        }
    };
};

export const updateReceiptAlert = (id: number, alert: ReceiptAlert) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.receiptAlert.updateAlert(DocumentType.Demand, id, alert);
        const isNew = !getState().demands.entity?.receiptAlert;
        if (response.success) {
            dispatch(demandAlertUpdated(alert));
            dispatch(
                Snackbar.enqueue(
                    PoolNotification.success(
                        isNew ? 'components.receiptAlert.snackbar.added' : 'components.receiptAlert.snackbar.changed'
                    )
                )
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('components.receiptAlert.snackbar.error')));
        }
    };
};

export const deleteReceiptAlert = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.receiptAlert.deleteAlert(DocumentType.Demand, id);
        if (response.success) {
            dispatch(demandAlertUpdated());
            dispatch(Snackbar.enqueue(PoolNotification.success('components.receiptAlert.snackbar.deleted')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('components.receiptAlert.snackbar.error')));
        }
    };
};

export const deleteDemand = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(deleteDemandAction(id));
        const response = await api.demands.delete(id);
        if (response.success === false) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const exportDemands = (filename: string, searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.demands.export(searchParams);
        if (response.success) {
            downloadFile(response.result, filename);
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const abortScheduledDemand = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.demands.abortSchedule(id);
        if (response.success) {
            dispatch(scheduledDemandAborted(id));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
